import axios from "axios";

// Create axios instance with Django CSRF configuration
const api = axios.create({
  baseURL: "/api/",
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add JWT token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401 AND 400 (token invalid) responses
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if it's a token-related error
    const isTokenError = () => {
      if (error.response?.status === 401) return true;

      if (error.response?.status === 400) {
        const errorData = error.response.data;

        // Handle different error response formats
        if (typeof errorData === "string") {
          return errorData.toLowerCase().includes("token");
        }

        if (typeof errorData === "object") {
          const errorString = JSON.stringify(errorData).toLowerCase();
          return (
            errorString.includes("token_not_valid") ||
            errorString.includes("token")
          );
        }
      }
      return true;
    };

    if (isTokenError()) {
      // Prevent infinite retry loops
      if (originalRequest._retry) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/login";
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refresh_token");

      if (refreshToken) {
        try {
          const response = await axios.post("/api/token/refresh/", {
            refresh: refreshToken,
          });

          localStorage.setItem("access_token", response.data.access);
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          return api.request(originalRequest);
        } catch (refreshError) {
          // Refresh token is also invalid - logout user
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available
        localStorage.removeItem("access_token");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Applications API
export const applicationsAPI = {
  list: () => api.get("applications/"),
  create: (data) => api.post("applications/", data),
  get: (id) => api.get(`applications/${id}/`),
  update: (id, data) => api.put(`applications/${id}/`, data),
  delete: (id) => api.delete(`applications/${id}/`),
  regenerateToken: (id) => api.post(`applications/${id}/regenerate_token/`),
};

// Groups API
export const groupsAPI = {
  list: () => api.get("groups/"),
  create: (data) => api.post("groups/", data),
  get: (id) => api.get(`groups/${id}/`),
  update: (id, data) => api.put(`groups/${id}/`, data),
  delete: (id) => api.delete(`groups/${id}/`),
};

// Clients API
export const clientsAPI = {
  list: () => api.get("clients/"),
  create: (data) => api.post("clients/", data),
  get: (id) => api.get(`clients/${id}/`),
  update: (id, data) => api.put(`clients/${id}/`, data),
  delete: (id) => api.delete(`clients/${id}/`),
};

// Logging Groups API
export const loggingGroupsAPI = {
  list: () => api.get("logging_groups/"),
  create: (data) => api.post("logging_groups/", data),
  get: (id) => api.get(`logging_groups/${id}/`),
  update: (id, data) => api.put(`logging_groups/${id}/`, data),
  delete: (id) => api.delete(`logging_groups/${id}/`),
  updatePartial: (id, data) => api.patch(`logging_groups/${id}/`, data),
  resolveLastState: (id) =>
    api.post(`logging_groups/${id}/resolve_last_state/`),
  getStaleErrors: (days = 7) =>
    api.get("logging_groups/stale_errors/", { params: { days } }),
  resolveStaleErrors: (days = 7) =>
    api.post("logging_groups/resolve_stale/", { days }),
};

// Messages API
export const messagesAPI = {
  list: (params = {}) => api.get("messages/", { params }),
  get: (id) => api.get(`messages/${id}/`),
  acknowledge: (id) => api.post(`messages/${id}/acknowledge/`),
};

// Notification Rules API
export const notificationRulesAPI = {
  list: () => api.get("notification_rules/"),
  create: (data) => api.post("notification_rules/", data),
  get: (id) => api.get(`notification_rules/${id}/`),
  update: (id, data) => api.put(`notification_rules/${id}/`, data),
  delete: (id) => api.delete(`notification_rules/${id}/`),
  acknowledge: (id) => api.post(`notification_rules/${id}/acknowledge/`),
};

// Alert Occurrences API
export const alertOccurrencesAPI = {
  // CHANGE THIS LINE: Add (params = {}) and pass it to axios
  list: (params = {}) => api.get("alert_occurrences/", { params }),
  get: (id) => api.get(`alert_occurrences/${id}/`),
  acknowledge: (id) => api.post(`alert_occurrences/${id}/acknowledge/`),
  acknowledgeAll: (loggingGroupId) =>
    api.post(`alert_occurrences/acknowledge_all/?logging_group_id=${loggingGroupId}`),
};

// Utils API
export const utilsAPI = {
  get: (endpoint) => api.get(`utils/${endpoint}/`),
  constants: () => api.get("utils/constants/"),
};

// Users API
export const usersAPI = {
  list: () => api.get("users/"),
  create: (data) => api.post("users/", data),
  get: (id) => api.get(`users/${id}/`),
  update: (id, data) => api.put(`users/${id}/`, data),
  delete: (id) => api.delete(`users/${id}/`),
  sendPasswordLink: (id) => api.post(`users/${id}/send_password_link/`),
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post("token/", credentials),
  refresh: (refreshToken) =>
    api.post("token/refresh/", { refresh: refreshToken }),
  me: () => api.get("auth/me/"),
  setPassword: (data) => api.post("auth/set_password/", data),
  changePassword: (data) => api.post("auth/change_password/", data),
};

export default api;
